import React from 'react';
import { MapPin, Ticket, Clock, ArrowRight } from 'lucide-react';
import { parseEventDate } from '../utils/dateUtils';
import './EventCard.css';

const EventCard = ({ event, onClick, onBuyTicket }) => {
    const {
        id,
        title,
        imageUrl,
        startTime,
        startDate, // fallback
        venueName,
        location,
        priceRange, // predefined string or calculated
        category,
        isFeatured
    } = event;

    const eventDate = parseEventDate(startTime || startDate);

    const getDateParts = (date) => {
        if (!date || isNaN(date.getTime())) {
            return { weekday: '', month: '', day: '' };
        }
        return {
            weekday: date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
            month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
            day: date.getDate()
        };
    };

    const formatTime = (date) => {
        if (!date || isNaN(date.getTime())) return 'Time TBA';
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const { weekday, month, day } = getDateParts(eventDate);
    const timeString = formatTime(eventDate);
    const venueDisplay = venueName || location || 'Venue TBA';

    const getEventImageStyle = (url) => ({
        backgroundImage: url
            ? `linear-gradient(180deg, rgba(5, 7, 14, 0.1), rgba(5, 7, 14, 0.9)), url(${url})`
            : 'linear-gradient(135deg, rgba(10, 84, 220, 0.9) 0%, rgba(0, 8, 32, 0.85) 100%)'
    });

    const getMinPrice = () => {
        if (event.ticketTypes && event.ticketTypes.length > 0) {
            const prices = event.ticketTypes
                .filter(t => t.status === 'ACTIVE' || !t.status) // Assume active if no status
                .map(t => t.priceAmount)
                .filter(p => p !== null && p !== undefined);

            if (prices.length > 0) {
                const minPrice = Math.min(...prices);
                const currency = event.ticketTypes[0].currencyCode || 'USD';

                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 0
                }).format(minPrice);
            }
        }
        return priceRange || 'Check Price';
    };

    const displayPrice = getMinPrice();

    return (
        <article className="event-card-cinematic" onClick={() => onClick(id)}>
            <div className="event-card-hero" style={getEventImageStyle(imageUrl)}>
                <div className="event-card-badges">
                    {isFeatured && <span className="badge featured">Featured</span>}
                    {category && <span className="badge glass">{category}</span>}
                </div>

                <div className="event-card-date-box">
                    <span className="month">{month}</span>
                    <span className="day">{day}</span>
                    <span className="weekday">{weekday}</span>
                </div>
            </div>

            <div className="event-card-content">
                <div className="event-card-header">
                    <h3 className="event-card-title">{title}</h3>
                    <div className="event-card-subtitle">
                        <MapPin size={14} className="icon-sub" />
                        <span>{venueDisplay}</span>
                    </div>
                </div>

                <div className="event-card-details">
                    <div className="detail-item">
                        <Clock size={16} className="icon-detail" />
                        <span>{timeString}</span>
                    </div>
                    <div className="detail-item price-item">
                        <Ticket size={16} className="icon-detail" />
                        <span>Start from <strong className="accent-text">{displayPrice}</strong></span>
                    </div>
                </div>

                <button
                    className="event-card-action"
                    onClick={(e) => {
                        e.stopPropagation();
                        onBuyTicket(id);
                    }}
                >
                    <span>Get Tickets</span>
                    <ArrowRight size={16} />
                </button>
            </div>
        </article>
    );
};

export default EventCard;
